import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import {
  ApiError,
  RANK_LABEL,
  api,
  canEdit,
  canManageUsers,
  type AuthUser,
  type MType,
  type Player,
  type PlayerChangeLogEntry,
  type PlayerFlag,
  type PlayerFlagKind,
  type Profession,
  type Rank,
  type SlotKey,
} from '@/lib/api';
import { IssueFlagDialog } from './PlayerFlagChip';
import { formatPower, todayISO } from '@/lib/utils';
import { COMMON_TIMEZONES } from '@/lib/serverTime';

const M_TYPES: MType[] = ['planes', 'rockets', 'tanks'];
const RANKS: Rank[] = ['R5', 'R4', 'R3', 'R2', 'R1'];
const PROFESSIONS: Profession[] = ['warlord', 'engineer'];

type Props = {
  playerId: number | 'new';
  user: AuthUser;
  onClose: () => void;
};

export default function PlayerDrawer({ playerId, user, onClose }: Props) {
  const isNew = playerId === 'new';
  const editable = canEdit(user.role);
  const qc = useQueryClient();

  const playerQuery = useQuery({
    queryKey: ['player', playerId],
    queryFn: () => api.getPlayer(playerId as number),
    enabled: !isNew,
  });

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <aside className="flex w-full max-w-2xl flex-col overflow-y-auto border-l border-slate-800 bg-slate-950 shadow-2xl">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-800 bg-slate-950/95 px-6 py-4 backdrop-blur">
          <h2 className="text-lg font-semibold">
            {isNew ? 'New player' : playerQuery.data?.name ?? 'Player'}
          </h2>
          <button
            onClick={onClose}
            className="rounded border border-slate-700 px-2 py-1 text-sm hover:bg-slate-800"
          >
            Close
          </button>
        </header>
        <div className="space-y-6 p-6">
          {isNew ? (
            <CreateForm
              editable={editable}
              onCreated={(p) => {
                qc.invalidateQueries({ queryKey: ['players'] });
                onClose();
                console.log('Created player', p.id);
              }}
            />
          ) : playerQuery.isLoading ? (
            <div className="text-slate-400">Loading…</div>
          ) : playerQuery.data ? (
            <ExistingPlayer
              player={playerQuery.data}
              editable={editable}
              canMove={canManageUsers(user.role)}
              onMoved={onClose}
            />
          ) : (
            <div className="text-slate-400">Player not found.</div>
          )}
        </div>
      </aside>
    </div>
  );
}

function CreateForm({
  editable,
  onCreated,
}: {
  editable: boolean;
  onCreated: (p: Player) => void;
}) {
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [realName, setRealName] = useState('');
  const [rank, setRank] = useState<Rank>('R1');
  const [profession, setProfession] = useState<Profession | ''>('');
  const [neverSub, setNeverSub] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.createPlayer({
        name,
        displayName: displayName.trim() ? displayName.trim() : null,
        realName: realName.trim() ? realName.trim() : null,
        rank,
        profession: profession || null,
        neverSub,
      }),
    onSuccess: onCreated,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to create'),
  });

  if (!editable) return <div className="text-slate-400">Insufficient permissions.</div>;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        mutation.mutate();
      }}
      className="space-y-4"
    >
      <Field label="In-game name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="input"
        />
      </Field>
      <Field label="Display name (optional)">
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Used in the UI instead of the game name"
          className="input"
        />
      </Field>
      <Field label="Real name (optional)">
        <input
          value={realName}
          onChange={(e) => setRealName(e.target.value)}
          placeholder="Real-life name, not shown in tables"
          className="input"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Rank">
          <select value={rank} onChange={(e) => setRank(e.target.value as Rank)} className="input">
            {RANKS.map((r) => (
              <option key={r} value={r}>
                {RANK_LABEL[r]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Profession">
          <select
            value={profession}
            onChange={(e) => setProfession(e.target.value as Profession | '')}
            className="input"
          >
            <option value="">— None —</option>
            {PROFESSIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={neverSub}
          onChange={(e) => setNeverSub(e.target.checked)}
        />
        Never-Sub (always a starter)
      </label>
      {error && <div className="text-sm text-red-400">{error}</div>}
      <button className="btn-primary" disabled={mutation.isPending}>
        {mutation.isPending ? 'Creating…' : 'Create player'}
      </button>
      <Style />
    </form>
  );
}

function ExistingPlayer({
  player,
  editable,
  canMove,
  onMoved,
}: {
  player: Player;
  editable: boolean;
  canMove: boolean;
  onMoved: () => void;
}) {
  return (
    <>
      <ExternalLinks player={player} editable={editable} />
      <CoreForm player={player} editable={editable} />
      <FlagsSection player={player} editable={editable} />
      <ChangeLogSection player={player} />
      <ParticipationHistorySection player={player} />
      <SnapshotsSection player={player} editable={editable} />
      <LeaveSection player={player} editable={editable} />
      <DangerZone player={player} editable={editable} />
      {canMove && !player.deleted && <MoveSection player={player} onMoved={onMoved} />}
      <Style />
    </>
  );
}

function ExternalLinks({ player, editable }: { player: Player; editable: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [pid, setPid] = useState(player.publicId?.toString() ?? '');
  const [error, setError] = useState<string | null>(null);
  const [syncMsg, setSyncMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    setPid(player.publicId?.toString() ?? '');
  }, [player.publicId]);

  const save = useMutation({
    mutationFn: () =>
      api.updatePlayer(player.id, {
        publicId: pid.trim() ? Number(pid.trim()) : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['players'] });
      qc.invalidateQueries({ queryKey: ['player', player.id] });
      setEditing(false);
      setError(null);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Failed to save LastRank ID'),
  });

  const syncNow = useMutation({
    mutationFn: () => api.syncPlayer(player.id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['players'] });
      qc.invalidateQueries({ queryKey: ['player', player.id] });
      qc.invalidateQueries({ queryKey: ['snapshots', player.id] });
      qc.invalidateQueries({ queryKey: ['name-history', player.id] });
      const parts: string[] = [];
      if (data.result.snapshotCreated) parts.push('new snapshot');
      if (data.result.nameChange)
        parts.push(`renamed → ${data.result.nameChange.newName}`);
      if (data.result.rankChange)
        parts.push(`rank → ${data.result.rankChange.newRank}`);
      if (data.result.professionUpdate)
        parts.push(`profession → ${data.result.professionUpdate.profession}`);
      setSyncMsg({
        kind: 'ok',
        text: parts.length ? `Synced: ${parts.join(', ')}.` : 'Synced — no changes.',
      });
    },
    onError: (err) =>
      setSyncMsg({
        kind: 'err',
        text: err instanceof ApiError ? err.message : 'Sync failed',
      }),
  });

  return (
    <section className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        {player.country && (
          <span className="text-slate-400">
            <span className="text-xs uppercase text-slate-500">Country</span>{' '}
            <span className="font-mono">{player.country}</span>
          </span>
        )}
        {player.baseLevel != null && (
          <span className="text-slate-400">
            <span className="text-xs uppercase text-slate-500">HQ</span>{' '}
            <span className="tabular-nums">{player.baseLevel}</span>
          </span>
        )}
        {player.lastSyncAt && (
          <span className="text-xs text-slate-500" title={player.lastSyncAt}>
            synced {player.lastSyncAt.slice(0, 10)}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {player.publicId && !editing && (
            <a
              href={`https://lastrank.fun/p/${player.publicId}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-indigo-300 hover:bg-slate-800"
            >
              LastRank ↗
            </a>
          )}
          {editable && !editing && (
            <button
              onClick={() => syncNow.mutate()}
              disabled={syncNow.isPending}
              title="Fetch latest data for this player from LastRank now"
              className="rounded border border-emerald-700 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-900/30 disabled:opacity-50"
            >
              {syncNow.isPending ? 'Syncing…' : 'Sync now'}
            </button>
          )}
          {editable && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-400 hover:bg-slate-800 hover:text-white"
            >
              {player.publicId ? 'Edit link' : 'Link to LastRank'}
            </button>
          )}
        </div>
      </div>
      {syncMsg && (
        <div
          className={
            syncMsg.kind === 'ok'
              ? 'rounded bg-emerald-900/30 px-2 py-1 text-xs text-emerald-200'
              : 'rounded bg-red-900/30 px-2 py-1 text-xs text-red-200'
          }
        >
          {syncMsg.text}
        </div>
      )}
      {editing && (
        <div className="flex flex-wrap items-end gap-2 border-t border-slate-800 pt-2">
          <label className="flex-1 text-xs text-slate-400">
            <div className="mb-1">LastRank public ID</div>
            <input
              value={pid}
              onChange={(e) => setPid(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="e.g. 2362068"
              className="input tabular-nums"
            />
            <div className="mt-1 text-[10px] text-slate-500">
              Find it on lastrank.fun — URL ends with /p/&lt;ID&gt;
            </div>
          </label>
          <div className="flex gap-1">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500 disabled:opacity-50"
            >
              Save
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setPid(player.publicId?.toString() ?? '');
              }}
              className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
            >
              Cancel
            </button>
          </div>
          {error && <div className="w-full text-xs text-red-400">{error}</div>}
        </div>
      )}
    </section>
  );
}

const FIELD_LABEL: Record<string, string> = {
  name: 'Name',
  displayName: 'Display name',
  realName: 'Real name',
  rank: 'Rank',
  profession: 'Profession',
  neverSub: 'Never-sub',
  publicId: 'LastRank ID',
  preferredCsSlot: 'CS slot',
  timezone: 'Timezone',
  active: 'Active',
  snapshot: 'Power snapshot',
};

function formatChangedBy(entry: PlayerChangeLogEntry): string {
  if (entry.source === 'lastrank') return 'LastRank sync';
  if (entry.source === 'self_report') return 'Player (self-report)';
  if (entry.changedByName) return entry.changedByName;
  if (entry.source && entry.source !== 'manual') return entry.source;
  return 'system';
}

function ChangeLogSection({ player }: { player: Player }) {
  const q = useQuery({
    queryKey: ['changelog', player.id],
    queryFn: () => api.listPlayerChangeLog(player.id),
  });
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">History</h3>
      {!q.data ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : q.data.length === 0 ? (
        <div className="text-xs text-slate-500">No changes recorded yet.</div>
      ) : (
        <ul className="space-y-1 text-xs">
          {q.data.map((h) => {
            const label = FIELD_LABEL[h.field] ?? h.field;
            const who = formatChangedBy(h);
            return (
              <li
                key={h.id}
                className="rounded border border-slate-800 bg-slate-950/40 px-2 py-1.5"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="tabular-nums text-slate-500">
                    {h.changedAt.slice(0, 10)}
                  </span>
                  <span className="font-medium text-slate-300">{label}</span>
                  <span className="text-slate-500">{h.oldValue ?? '—'}</span>
                  <span className="text-slate-600">→</span>
                  <span className="text-slate-200">{h.newValue ?? '—'}</span>
                  <span className="ml-auto text-[10px] uppercase text-slate-500">
                    by {who}
                  </span>
                </div>
                {h.comment && (
                  <div className="mt-0.5 text-[11px] text-slate-400">“{h.comment}”</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function ParticipationHistorySection({ player }: { player: Player }) {
  const query = useQuery({
    queryKey: ['history', player.id],
    queryFn: () => api.playerHistory(player.id, 15),
  });
  const data = query.data;
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">
        Participation history
      </h3>
      {!data ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <Metric
              value={data.metrics?.consecutiveNotPicked ?? 0}
              label="Not picked"
              accent="orange"
            />
            <Metric
              value={data.metrics?.consecutiveNoShows ?? 0}
              label="No-shows"
              accent="red"
            />
            <Metric
              value={data.metrics?.consecutiveSubsOnly ?? 0}
              label="Subs in a row"
              accent="amber"
            />
          </div>
          {data.events.length === 0 ? (
            <div className="text-sm text-slate-500">No events yet.</div>
          ) : (
            <ul className="space-y-1">
              {data.events.map((ev) => (
                <HistoryRow key={ev.id} item={ev} />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function Metric({
  value,
  label,
  accent,
}: {
  value: number;
  label: string;
  accent: 'orange' | 'red' | 'amber';
}) {
  const bg = {
    orange: 'bg-orange-900/20 text-orange-300',
    red: 'bg-red-900/20 text-red-300',
    amber: 'bg-amber-900/20 text-amber-300',
  }[accent];
  return (
    <div className={`rounded ${bg} p-2`}>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
    </div>
  );
}

function HistoryRow({
  item,
}: {
  item: import('@/lib/api').ParticipationHistoryItem;
}) {
  const typeShort = item.event.type === 'canyon_storm' ? 'CS' : 'DS';
  const outcome = (() => {
    if (item.event.status === 'skipped') return { label: 'Skipped', cls: 'text-slate-500' };
    if (!item.appliedA && !item.appliedB)
      return { label: 'Did not apply', cls: 'text-slate-600' };
    if (item.assignedRole == null)
      return { label: 'Not picked', cls: 'text-orange-400' };
    if (item.attendanceStatus === 'no_show')
      return { label: 'No-show', cls: 'text-red-400' };
    if (item.attendanceStatus === 'late')
      return { label: 'Late', cls: 'text-yellow-400' };
    if (item.assignedRole === 'starter')
      return { label: 'Played starter', cls: 'text-emerald-400' };
    return { label: 'Subbed', cls: 'text-amber-400' };
  })();
  return (
    <li className="flex items-center gap-2 rounded border border-slate-800 bg-slate-950/40 px-2 py-1.5 text-xs">
      <span className="w-10 font-mono text-slate-500">{typeShort}</span>
      <span className="w-20 tabular-nums text-slate-400">{item.event.eventDate}</span>
      <span className={`flex-1 ${outcome.cls}`}>{outcome.label}</span>
      {(item.appliedA || item.appliedB) && (
        <span className="text-slate-500">
          applied {item.appliedA && item.appliedB ? 'A+B' : item.appliedA ? 'A' : 'B'}
        </span>
      )}
      {item.assignedSlot && (
        <span className="text-slate-400">→ {item.assignedSlot}</span>
      )}
    </li>
  );
}

function CoreForm({ player, editable }: { player: Player; editable: boolean }) {
  const [name, setName] = useState(player.name);
  const [displayName, setDisplayName] = useState(player.displayName ?? '');
  const [realName, setRealName] = useState(player.realName ?? '');
  const [rank, setRank] = useState<Rank>(player.rank);
  const [profession, setProfession] = useState<Profession | ''>(player.profession ?? '');
  const [neverSub, setNeverSub] = useState(player.neverSub);
  const [preferredCsSlot, setPreferredCsSlot] = useState<SlotKey | ''>(
    player.preferredCsSlot ?? '',
  );
  const [timezone, setTimezone] = useState<string>(player.timezone ?? '');
  const [comment, setComment] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setName(player.name);
    setDisplayName(player.displayName ?? '');
    setRealName(player.realName ?? '');
    setRank(player.rank);
    setProfession(player.profession ?? '');
    setNeverSub(player.neverSub);
    setPreferredCsSlot(player.preferredCsSlot ?? '');
    setTimezone(player.timezone ?? '');
  }, [player.id, player.name, player.displayName, player.realName, player.rank, player.profession, player.neverSub, player.preferredCsSlot, player.timezone]);

  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () =>
      api.updatePlayer(player.id, {
        name,
        displayName: displayName.trim() ? displayName.trim() : null,
        realName: realName.trim() ? realName.trim() : null,
        rank,
        profession: profession || null,
        neverSub,
        preferredCsSlot: preferredCsSlot === '' ? null : preferredCsSlot,
        timezone: timezone.trim() ? timezone.trim() : null,
        comment: comment.trim() ? comment.trim() : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['players'] });
      qc.invalidateQueries({ queryKey: ['player', player.id] });
      qc.invalidateQueries({ queryKey: ['changelog', player.id] });
      setComment('');
      setSavedAt(Date.now());
    },
  });

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">Player</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="In-game name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!editable}
            className="input"
          />
        </Field>
        <Field label="Display name (optional)">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={!editable}
            placeholder="Used in the UI instead of the game name"
            className="input"
          />
        </Field>
      </div>
      <Field label="Real name (optional)">
        <input
          value={realName}
          onChange={(e) => setRealName(e.target.value)}
          disabled={!editable}
          placeholder="Real-life name, not shown in tables"
          className="input"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Rank">
          <select
            value={rank}
            onChange={(e) => setRank(e.target.value as Rank)}
            disabled={!editable}
            className="input"
          >
            {RANKS.map((r) => (
              <option key={r} value={r}>
                {RANK_LABEL[r]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Profession">
          <select
            value={profession}
            onChange={(e) => setProfession(e.target.value as Profession | '')}
            disabled={!editable}
            className="input"
          >
            <option value="">— None —</option>
            {PROFESSIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={neverSub}
          onChange={(e) => setNeverSub(e.target.checked)}
          disabled={!editable}
        />
        Never-Sub (always a starter)
      </label>
      <Field label="CS slot preference">
        <select
          value={preferredCsSlot}
          onChange={(e) => setPreferredCsSlot(e.target.value as SlotKey | '')}
          disabled={!editable}
          className="input"
        >
          <option value="">— No preference —</option>
          <option value="A">Slot A</option>
          <option value="B">Slot B</option>
        </select>
      </Field>
      <Field label="Timezone (IANA, e.g. Europe/Berlin)">
        <input
          list="tz-suggestions"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          disabled={!editable}
          placeholder="Europe/Berlin"
          className="input"
        />
        <datalist id="tz-suggestions">
          {COMMON_TIMEZONES.map((tz) => (
            <option key={tz} value={tz} />
          ))}
        </datalist>
      </Field>
      {editable && (
        <>
          <Field label="Comment (optional)">
            <input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Why is this changing? Shown in the history."
              className="input"
            />
          </Field>
          <div className="flex items-center gap-3">
            <button className="btn-primary" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </button>
            {savedAt && <span className="text-xs text-slate-500">Saved.</span>}
          </div>
        </>
      )}
    </section>
  );
}

function SnapshotsSection({ player, editable }: { player: Player; editable: boolean }) {
  const snapshots = useQuery({
    queryKey: ['snapshots', player.id],
    queryFn: () => api.listSnapshots(player.id),
  });
  const [form, setForm] = useState({
    totalPower: '',
    kills: '',
    m1Power: '',
    m1Type: 'planes' as MType,
    m2Power: '',
    m2Type: 'rockets' as MType,
    m3Power: '',
    m3Type: 'tanks' as MType,
  });

  const qc = useQueryClient();
  const addMutation = useMutation({
    mutationFn: () =>
      api.addSnapshot(player.id, {
        totalPower: Number(form.totalPower),
        kills: Number(form.kills),
        m1Power: Number(form.m1Power),
        m1Type: form.m1Type,
        m2Power: Number(form.m2Power),
        m2Type: form.m2Type,
        m3Power: Number(form.m3Power),
        m3Type: form.m3Type,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['snapshots', player.id] });
      qc.invalidateQueries({ queryKey: ['players'] });
      qc.invalidateQueries({ queryKey: ['player', player.id] });
      setForm((f) => ({ ...f, totalPower: '', kills: '', m1Power: '', m2Power: '', m3Power: '' }));
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (snapshotId: number) => api.deleteSnapshot(snapshotId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['snapshots', player.id] });
      qc.invalidateQueries({ queryKey: ['players'] });
      qc.invalidateQueries({ queryKey: ['player', player.id] });
      qc.invalidateQueries({ queryKey: ['changelog', player.id] });
    },
  });

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">Power</h3>

      {editable && (
        <div className="space-y-3 rounded border border-slate-800 bg-slate-900/50 p-3">
          <div className="text-xs text-slate-500">Add new snapshot</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Total Power">
              <input
                type="number"
                step="0.1"
                value={form.totalPower}
                onChange={(e) => setForm({ ...form, totalPower: e.target.value })}
                className="input"
                required
              />
            </Field>
            <Field label="Kills">
              <input
                type="number"
                step="0.1"
                value={form.kills}
                onChange={(e) => setForm({ ...form, kills: e.target.value })}
                className="input"
                required
              />
            </Field>
          </div>
          {(['m1', 'm2', 'm3'] as const).map((slot) => (
            <div key={slot} className="grid grid-cols-2 gap-3">
              <Field label={`${slot.toUpperCase()} Power`}>
                <input
                  type="number"
                  step="0.1"
                  value={form[`${slot}Power` as const]}
                  onChange={(e) => setForm({ ...form, [`${slot}Power`]: e.target.value } as typeof form)}
                  className="input"
                  required
                />
              </Field>
              <Field label={`${slot.toUpperCase()} Type`}>
                <select
                  value={form[`${slot}Type` as const]}
                  onChange={(e) =>
                    setForm({ ...form, [`${slot}Type`]: e.target.value as MType } as typeof form)
                  }
                  className="input"
                >
                  {M_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          ))}
          <button
            className="btn-primary"
            onClick={() => addMutation.mutate()}
            disabled={addMutation.isPending}
          >
            {addMutation.isPending ? 'Saving…' : 'Save snapshot'}
          </button>
        </div>
      )}

      {snapshots.data && snapshots.data.length >= 2 && (
        <GrowthChart snapshots={snapshots.data} />
      )}

      <div className="space-y-1">
        <div className="text-xs uppercase text-slate-500">History</div>
        {snapshots.data?.length ? (
          <SnapshotHistoryTable
            rows={snapshots.data}
            onDelete={
              editable
                ? (s) => {
                    if (
                      !confirm(
                        `Delete the snapshot from ${s.recordedAt.slice(0, 10)} ` +
                          `(${formatPower(s.totalPower)} total)? This cannot be undone.`,
                      )
                    )
                      return;
                    deleteMutation.mutate(s.id);
                  }
                : undefined
            }
            deletingId={deleteMutation.isPending ? deleteMutation.variables : null}
          />
        ) : (
          <div className="text-sm text-slate-500">No snapshots yet.</div>
        )}
        {deleteMutation.isError && (
          <div className="text-xs text-red-400">
            {(deleteMutation.error as Error)?.message ?? 'Delete failed'}
          </div>
        )}
      </div>
    </section>
  );
}

function LeaveSection({ player, editable }: { player: Player; editable: boolean }) {
  const leaves = useQuery({
    queryKey: ['leaves', player.id],
    queryFn: () => api.listLeaves(player.id),
  });
  const [startsAt, setStartsAt] = useState(todayISO());
  const [endsAt, setEndsAt] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['leaves', player.id] });
    qc.invalidateQueries({ queryKey: ['players'] });
    qc.invalidateQueries({ queryKey: ['player', player.id] });
  };

  const addMutation = useMutation({
    mutationFn: () =>
      api.addLeave(player.id, {
        startsAt,
        endsAt: endsAt || null,
        reason: reason || undefined,
      }),
    onSuccess: () => {
      invalidate();
      setEndsAt('');
      setReason('');
      setError(null);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Failed to add leave'),
  });

  const endNow = useMutation({
    mutationFn: (id: number) => api.updateLeave(id, { endsAt: todayISO() }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteLeave(id),
    onSuccess: invalidate,
  });

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">Leave</h3>
      {player.currentLeave ? (
        <div className="rounded border border-amber-700 bg-amber-900/30 p-3 text-sm">
          <div>
            On leave from <strong>{player.currentLeave.startsAt}</strong>
            {player.currentLeave.endsAt ? (
              <>
                {' '}
                until <strong>{player.currentLeave.endsAt}</strong>
              </>
            ) : (
              ' (open-ended)'
            )}
            {player.currentLeave.reason && (
              <div className="text-xs text-amber-200/80">Reason: {player.currentLeave.reason}</div>
            )}
          </div>
          {editable && (
            <button
              className="mt-2 rounded border border-amber-700 px-2 py-1 text-xs hover:bg-amber-900/60"
              onClick={() => endNow.mutate(player.currentLeave!.id)}
            >
              End leave today
            </button>
          )}
        </div>
      ) : (
        <div className="text-sm text-slate-400">Currently not on leave.</div>
      )}

      {editable && (
        <div className="space-y-3 rounded border border-slate-800 bg-slate-900/50 p-3">
          <div className="text-xs text-slate-500">Schedule leave</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From">
              <input
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Until (optional)">
              <input
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="input"
              />
            </Field>
          </div>
          <Field label="Reason (optional)">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="input"
            />
          </Field>
          {error && <div className="text-sm text-red-400">{error}</div>}
          <button
            className="btn-primary"
            onClick={() => addMutation.mutate()}
            disabled={addMutation.isPending || !startsAt}
          >
            {addMutation.isPending ? 'Saving…' : 'Add leave'}
          </button>
        </div>
      )}

      <div className="space-y-1">
        <div className="text-xs uppercase text-slate-500">History</div>
        {leaves.data?.length ? (
          <ul className="space-y-1 text-sm">
            {leaves.data.map((l) => (
              <li
                key={l.id}
                className="flex items-center justify-between rounded border border-slate-800 px-3 py-1.5"
              >
                <span>
                  {l.startsAt} → {l.endsAt ?? '∞'}
                  {l.reason && <span className="ml-2 text-slate-500">· {l.reason}</span>}
                </span>
                {editable && (
                  <button
                    onClick={() => remove.mutate(l.id)}
                    className="text-xs text-slate-500 hover:text-red-400"
                  >
                    Delete
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-slate-500">No leave history.</div>
        )}
      </div>
    </section>
  );
}

function MoveSection({ player, onMoved }: { player: Player; onMoved: () => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [targetId, setTargetId] = useState<number | ''>('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const targetsQuery = useQuery({
    queryKey: ['player-move-targets'],
    queryFn: api.listPlayerMoveTargets,
    enabled: open,
  });
  const targets = targetsQuery.data ?? [];
  const target = targets.find((t) => t.id === targetId);
  const move = useMutation({
    mutationFn: () =>
      api.movePlayer(player.id, {
        targetAllianceId: targetId as number,
        comment: comment.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['players'] });
      qc.invalidateQueries({ queryKey: ['player', player.id] });
      onMoved();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  return (
    <section className="space-y-2 border-t border-slate-800 pt-4">
      <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">
        Move to another alliance
      </h3>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
        >
          Move to alliance…
        </button>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">
            Copies master data, power history, name history, leaves, magic link and push
            subscriptions to the target alliance and deactivates the player here. Event, VS and
            survey history stays with this alliance.
          </p>
          {targetsQuery.isLoading ? (
            <div className="text-sm text-slate-400">Loading…</div>
          ) : targets.length === 0 ? (
            <div className="text-sm text-slate-400">
              No other alliance on this server uses the tool yet.
            </div>
          ) : (
            <>
              <select
                className="input"
                value={targetId}
                onChange={(e) => setTargetId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <option value="">Select alliance…</option>
                {targets.map((t) => (
                  <option key={t.id} value={t.id}>
                    #{t.serverNumber ?? '—'} · {t.name}
                    {t.tag ? ` [${t.tag}]` : ''}
                  </option>
                ))}
              </select>
              <input
                className="input"
                placeholder="Comment (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={500}
              />
              <div className="flex gap-2">
                <button
                  disabled={!target || move.isPending}
                  onClick={() => {
                    if (!target) return;
                    if (!confirm(`Move ${player.name} to ${target.tag || target.name}?`)) return;
                    setError(null);
                    move.mutate();
                  }}
                  className="rounded border border-amber-700 px-3 py-1.5 text-sm text-amber-300 hover:bg-amber-900/40 disabled:opacity-40"
                >
                  {move.isPending ? 'Moving…' : 'Move player'}
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
          {error && <div className="text-sm text-red-400">{error}</div>}
        </div>
      )}
    </section>
  );
}

function DangerZone({ player, editable }: { player: Player; editable: boolean }) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['players'] });
    qc.invalidateQueries({ queryKey: ['player', player.id] });
    qc.invalidateQueries({ queryKey: ['changelog', player.id] });
  };
  const deactivate = useMutation({
    mutationFn: (comment: string | null) => api.deactivatePlayer(player.id, comment),
    onSuccess: invalidate,
  });
  const reactivate = useMutation({
    mutationFn: (comment: string | null) => api.reactivatePlayer(player.id, comment),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (comment: string | null) => api.deletePlayer(player.id, comment),
    onSuccess: invalidate,
  });
  const restore = useMutation({
    mutationFn: () => api.undeletePlayer(player.id),
    onSuccess: invalidate,
  });

  if (!editable) return null;
  return (
    <section className="space-y-2 border-t border-slate-800 pt-4">
      <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">Status</h3>
      {player.active ? (
        <button
          onClick={() => {
            if (!confirm(`Deactivate ${player.name}? They will be hidden from default views.`)) return;
            const c = prompt('Comment (optional):', '') ?? '';
            deactivate.mutate(c.trim() || null);
          }}
          className="rounded border border-red-700 px-3 py-1.5 text-sm text-red-300 hover:bg-red-900/40"
        >
          Deactivate player
        </button>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              const c = prompt('Comment (optional):', '') ?? '';
              reactivate.mutate(c.trim() || null);
            }}
            className="rounded border border-emerald-700 px-3 py-1.5 text-sm text-emerald-300 hover:bg-emerald-900/40"
          >
            Reactivate player
          </button>
          {player.deleted ? (
            <button
              onClick={() => restore.mutate()}
              className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
            >
              Restore from deleted
            </button>
          ) : (
            <button
              onClick={() => {
                if (
                  !confirm(
                    `Delete ${player.name}? They will disappear from every list — historical records stay linked.`,
                  )
                )
                  return;
                const c = prompt('Comment (optional):', '') ?? '';
                remove.mutate(c.trim() || null);
              }}
              className="rounded border border-red-800 px-3 py-1.5 text-sm text-red-400 hover:bg-red-950/40"
            >
              Delete player
            </button>
          )}
        </div>
      )}
    </section>
  );
}

const FLAG_KIND_LABEL: Record<PlayerFlagKind, string> = {
  red: 'Red card',
  yellow: 'Yellow card',
  green: 'Should be picked',
  train_hint: 'Train hint',
};

function FlagKindBadge({ kind }: { kind: PlayerFlagKind }) {
  const cls = {
    red: 'bg-red-500/20 text-red-300',
    yellow: 'bg-yellow-500/20 text-yellow-200',
    green: 'bg-emerald-500/20 text-emerald-200',
    train_hint: 'bg-sky-500/20 text-sky-200',
  }[kind];
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${cls}`}>
      {FLAG_KIND_LABEL[kind]}
    </span>
  );
}

function FlagsSection({ player, editable }: { player: Player; editable: boolean }) {
  const qc = useQueryClient();
  const [issuing, setIssuing] = useState(false);
  const q = useQuery({
    queryKey: ['playerFlags', player.id],
    queryFn: () => api.listPlayerFlags(player.id),
  });
  const revoke = useMutation({
    mutationFn: (flagId: number) => api.revokePlayerFlag(player.id, flagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['players'] });
      qc.invalidateQueries({ queryKey: ['player', player.id] });
      qc.invalidateQueries({ queryKey: ['playerFlags', player.id] });
      qc.invalidateQueries({ queryKey: ['activeFlags'] });
    },
  });
  const active = q.data?.active ?? [];
  const history = q.data?.history ?? [];
  const now = Date.now();

  const fmtExpiry = (iso: string | null) => {
    if (!iso) return 'no expiry';
    const t = new Date(iso).getTime();
    const days = Math.round((t - now) / (24 * 60 * 60 * 1000));
    if (days < 0) return 'expired';
    if (days === 0) return 'today';
    if (days === 1) return 'tomorrow';
    return `in ${days} d`;
  };

  const renderRow = (f: PlayerFlag, isActive: boolean) => (
    <li
      key={f.id}
      className="flex items-start gap-2 rounded border border-slate-800 bg-slate-950/40 px-2 py-1.5 text-xs"
    >
      <FlagKindBadge kind={f.kind} />
      <div className="flex-1">
        {f.note && <div className="whitespace-pre-wrap text-slate-200">{f.note}</div>}
        <div className="mt-0.5 text-[10px] text-slate-500">
          {f.issuedAt.slice(0, 10)} · {isActive ? fmtExpiry(f.expiresAt) : f.revokedAt ? 'revoked' : 'expired'}
        </div>
      </div>
      {editable && isActive && (
        <button
          onClick={() => revoke.mutate(f.id)}
          disabled={revoke.isPending}
          className="text-[11px] text-slate-500 hover:text-red-400"
        >
          Revoke
        </button>
      )}
    </li>
  );

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">
          Flags &amp; cards
        </h3>
        {editable && (
          <button
            onClick={() => setIssuing(true)}
            className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
          >
            Issue flag
          </button>
        )}
      </div>
      {active.length === 0 ? (
        <div className="text-xs text-slate-500">No active flags.</div>
      ) : (
        <ul className="space-y-1">{active.map((f) => renderRow(f, true))}</ul>
      )}
      {history.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-500">History ({history.length})</summary>
          <ul className="mt-1 space-y-1">{history.map((f) => renderRow(f, false))}</ul>
        </details>
      )}
      {issuing && <IssueFlagDialog playerId={player.id} onClose={() => setIssuing(false)} />}
    </section>
  );
}

function SnapshotHistoryTable({
  rows,
  onDelete,
  deletingId,
}: {
  rows: import('@/lib/api').PowerSnapshot[];
  onDelete?: (s: import('@/lib/api').PowerSnapshot) => void;
  deletingId?: number | null;
}) {
  // Rows arrive sorted DESC by recordedAt. Collapse consecutive rows where
  // M1/M2/M3 (and totalPower) are identical — those don't reflect a change
  // and just clutter the history.
  const filtered = useMemo(() => {
    const out: typeof rows = [];
    for (let i = 0; i < rows.length; i++) {
      const cur = rows[i];
      const older = rows[i + 1];
      const changed =
        !older ||
        older.m1Power !== cur.m1Power ||
        older.m2Power !== cur.m2Power ||
        older.m3Power !== cur.m3Power ||
        older.m1Type !== cur.m1Type ||
        older.m2Type !== cur.m2Type ||
        older.m3Type !== cur.m3Type ||
        older.totalPower !== cur.totalPower ||
        older.kills !== cur.kills;
      if (changed) out.push(cur);
    }
    return out;
  }, [rows]);
  const skipped = rows.length - filtered.length;
  return (
    <div className="overflow-hidden rounded border border-slate-800">
      <table className="w-full text-xs">
        <thead className="bg-slate-900 text-slate-400">
          <tr>
            <th className="px-2 py-1 text-left">Date</th>
            <th className="px-2 py-1 text-left">Total</th>
            <th className="px-2 py-1 text-left">Kills</th>
            <th className="px-2 py-1 text-left">M1</th>
            <th className="px-2 py-1 text-left">M2</th>
            <th className="px-2 py-1 text-left">M3</th>
            <th className="px-2 py-1 text-left">Source</th>
            {onDelete && <th className="w-6 px-2 py-1" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {filtered.map((s) => (
            <tr key={s.id}>
              <td className="px-2 py-1 text-slate-400">
                {s.recordedAt.slice(0, 10)}
              </td>
              <td className="px-2 py-1 tabular-nums">{formatPower(s.totalPower)}</td>
              <td className="px-2 py-1 tabular-nums">{formatPower(s.kills)}</td>
              <td className="px-2 py-1">
                {formatPower(s.m1Power)} <span className="text-slate-500">{s.m1Type}</span>
              </td>
              <td className="px-2 py-1">
                {formatPower(s.m2Power)} <span className="text-slate-500">{s.m2Type}</span>
              </td>
              <td className="px-2 py-1">
                {formatPower(s.m3Power)} <span className="text-slate-500">{s.m3Type}</span>
              </td>
              <td className="px-2 py-1">
                <SnapshotSourceBadge source={s.source ?? null} />
              </td>
              {onDelete && (
                <td className="px-2 py-1 text-right">
                  <button
                    onClick={() => onDelete(s)}
                    disabled={deletingId === s.id}
                    title="Delete snapshot"
                    aria-label="Delete snapshot"
                    className="text-slate-500 hover:text-red-400 disabled:opacity-40"
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {skipped > 0 && (
        <div className="border-t border-slate-800 bg-slate-900/40 px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500">
          {skipped} unchanged snapshot{skipped === 1 ? '' : 's'} hidden
        </div>
      )}
    </div>
  );
}

function SnapshotSourceBadge({ source }: { source: string | null }) {
  if (!source || source === 'manual')
    return <span className="text-slate-500">manual</span>;
  if (source === 'self_report')
    return (
      <span className="rounded bg-indigo-900/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-indigo-200">
        self-report
      </span>
    );
  if (source === 'strength_request')
    return (
      <span className="rounded bg-emerald-900/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-200">
        strength req.
      </span>
    );
  if (source === 'video_import_kills')
    return (
      <span className="rounded bg-rose-900/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-rose-200">
        video kills
      </span>
    );
  if (source === 'video_import_power')
    return (
      <span className="rounded bg-sky-900/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-sky-200">
        video power
      </span>
    );
  if (source === 'self_report_kills')
    return (
      <span className="rounded bg-red-900/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-red-200">
        self-report kills
      </span>
    );
  if (source === 'lastrank')
    return <span className="text-slate-500">LastRank</span>;
  return <span className="text-slate-500">{source}</span>;
}

function GrowthChart({
  snapshots,
}: {
  snapshots: import('@/lib/api').PowerSnapshot[];
}) {
  type SeriesKey = 'mSum' | 'm1' | 'm2' | 'm3' | 'kills';
  const [visible, setVisible] = useState<Record<SeriesKey, boolean>>({
    mSum: true,
    m1: true,
    m2: true,
    m3: true,
    kills: false,
  });

  const points = useMemo(() => {
    return [...snapshots]
      .sort((a, b) => a.recordedAt.localeCompare(b.recordedAt))
      .map((s) => ({
        t: new Date(s.recordedAt).getTime(),
        date: s.recordedAt.slice(0, 10),
        m1: s.m1Power,
        m2: s.m2Power,
        m3: s.m3Power,
        mSum: s.m1Power + s.m2Power + s.m3Power,
        kills: s.kills,
      }));
  }, [snapshots]);

  const series: { key: SeriesKey; label: string; color: string }[] = [
    { key: 'mSum', label: 'M-Sum', color: '#818cf8' },
    { key: 'm1', label: 'M1', color: '#34d399' },
    { key: 'm2', label: 'M2', color: '#f59e0b' },
    { key: 'm3', label: 'M3', color: '#f472b6' },
    { key: 'kills', label: 'Kills', color: '#ef4444' },
  ];

  const W = 560;
  const H = 200;
  const PAD_L = 40;
  const PAD_R = 12;
  const PAD_T = 12;
  const PAD_B = 28;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const activeKeys = series.filter((s) => visible[s.key]).map((s) => s.key);
  const tMin = points[0].t;
  const tMax = points[points.length - 1].t;
  const tSpan = Math.max(1, tMax - tMin);
  let vMin = Infinity;
  let vMax = -Infinity;
  for (const p of points) {
    for (const k of activeKeys) {
      const v = p[k];
      if (v < vMin) vMin = v;
      if (v > vMax) vMax = v;
    }
  }
  if (!Number.isFinite(vMin) || !Number.isFinite(vMax)) {
    vMin = 0;
    vMax = 1;
  }
  if (vMax === vMin) vMax = vMin + 1;
  const vSpan = vMax - vMin;
  const pad = vSpan * 0.08;
  vMin -= pad;
  vMax += pad;

  const x = (t: number) => PAD_L + ((t - tMin) / tSpan) * innerW;
  const y = (v: number) => PAD_T + innerH - ((v - vMin) / (vMax - vMin)) * innerH;

  const buildPath = (key: SeriesKey) =>
    points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p.t).toFixed(1)} ${y(p[key]).toFixed(1)}`)
      .join(' ');

  const yTicks = 4;
  const ticks = Array.from({ length: yTicks + 1 }, (_, i) => vMin + (vSpan + 2 * pad) * (i / yTicks));

  const first = points[0];
  const last = points[points.length - 1];
  const totals = series.map((s) => ({
    ...s,
    delta: last[s.key] - first[s.key],
  }));

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">
          Growth
        </h3>
        <span className="text-[10px] text-slate-500">
          {first.date} → {last.date}
        </span>
      </div>
      <div className="rounded border border-slate-800 bg-slate-950/40 p-2">
        <div className="overflow-x-auto">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full"
            style={{ maxWidth: '100%', height: 'auto' }}
            role="img"
            aria-label="Growth chart"
          >
            {ticks.map((tv, i) => (
              <g key={i}>
                <line
                  x1={PAD_L}
                  x2={W - PAD_R}
                  y1={y(tv)}
                  y2={y(tv)}
                  stroke="#1e293b"
                  strokeWidth={1}
                />
                <text
                  x={PAD_L - 6}
                  y={y(tv) + 3}
                  fontSize={9}
                  textAnchor="end"
                  fill="#64748b"
                >
                  {tv.toFixed(0)}
                </text>
              </g>
            ))}
            {[first, last].map((p, i) => (
              <text
                key={i}
                x={x(p.t)}
                y={H - 8}
                fontSize={9}
                textAnchor={i === 0 ? 'start' : 'end'}
                fill="#64748b"
              >
                {p.date}
              </text>
            ))}
            {series
              .filter((s) => visible[s.key])
              .map((s) => (
                <g key={s.key}>
                  <path
                    d={buildPath(s.key)}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={1.75}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                  {points.map((p, i) => (
                    <circle
                      key={i}
                      cx={x(p.t)}
                      cy={y(p[s.key])}
                      r={2}
                      fill={s.color}
                    >
                      <title>
                        {s.label} · {p.date} · {p[s.key].toFixed(1)}
                      </title>
                    </circle>
                  ))}
                </g>
              ))}
          </svg>
        </div>
        <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
          {totals.map((s) => {
            const sign = s.delta > 0 ? '+' : s.delta < 0 ? '−' : '±';
            const positive = s.delta > 0;
            return (
              <button
                key={s.key}
                onClick={() => setVisible((v) => ({ ...v, [s.key]: !v[s.key] }))}
                className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 transition ${
                  visible[s.key]
                    ? 'border-slate-700 bg-slate-900/60'
                    : 'border-slate-800 bg-slate-950/40 opacity-40'
                }`}
              >
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: s.color }}
                />
                <span className="text-slate-300">{s.label}</span>
                <span
                  className={`tabular-nums ${
                    Math.abs(s.delta) < 0.05
                      ? 'text-slate-500'
                      : positive
                        ? 'text-emerald-300'
                        : 'text-red-300'
                  }`}
                >
                  {sign}
                  {Math.abs(s.delta).toFixed(1)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="text-xs text-slate-400">{label}</span>
      {children}
    </label>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        width: 100%;
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
      }
      .input:focus { outline: none; border-color: rgb(100 116 139 / 1); }
      .input:disabled { opacity: 0.6; }
      .btn-primary {
        background: rgb(79 70 229 / 1);
        color: white;
        padding: 6px 12px;
        font-size: 14px;
        border-radius: 6px;
      }
      .btn-primary:hover { background: rgb(99 102 241 / 1); }
      .btn-primary:disabled { opacity: 0.5; }
    `}</style>
  );
}
